'use client'

import { useState } from 'react';
import { usePostCreationStore } from '@/lib/stores/postCreationStore';
import { DeepDivePost, PostSection } from '@/lib/types/posts';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Typography from '@tiptap/extension-typography';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { 
  Bold, 
  Italic, 
  Link as LinkIcon, 
  List, 
  ListOrdered, 
  PlusCircle, 
  ArrowUp, 
  ArrowDown, 
  Trash2 
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';

export function DeepDiveEditor() {
  const { currentPost, updatePostField, addSection, updateSection, deleteSection, moveSection } = usePostCreationStore();
  const deepDivePost = currentPost as DeepDivePost;
  const sections = deepDivePost.sections || [];
  
  const [expandedSection, setExpandedSection] = useState<string | null>(sections[0]?.id || null);
  
  // Editor for the main post excerpt
  const excerptEditor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Write a brief excerpt for your deep dive...',
      }),
      Link.configure({
        openOnClick: false,
      }),
      Typography,
    ],
    content: deepDivePost.excerpt || '',
    onUpdate: ({ editor }) => {
      updatePostField('excerpt', editor.getHTML());
    },
    immediatelyRender: false,
  });
  
  const handleAddSection = () => {
    const newSection: Omit<PostSection, 'id'> = {
      title: 'New Section',
      content: '',
      displayOrder: sections.length,
    };
    
    addSection(newSection);
  };
  
  const handleSectionContentChange = (sectionId: string, content: string) => {
    updateSection(sectionId, { content });
  };
  
  const handleSectionTitleChange = (sectionId: string, title: string) => {
    updateSection(sectionId, { title });
  };
  
  const handleMoveSection = (sectionId: string, direction: 'up' | 'down') => {
    moveSection(sectionId, direction);
  };
  
  const handleDeleteSection = (sectionId: string) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      deleteSection(sectionId);
    }
  };
  
  const toggleExpandSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };
  
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          placeholder="Enter deep dive title"
          value={deepDivePost.title || ''}
          onChange={(e) => updatePostField('title', e.target.value)}
          className="text-xl"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="excerpt">Excerpt</Label>
        <div className="border rounded-md p-4 min-h-[100px]">
          <EditorContent editor={excerptEditor} />
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Label>Sections</Label>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleAddSection}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Section
          </Button>
        </div>
        
        <ScrollArea className="h-[500px] rounded-md border">
          <div className="p-4 space-y-4">
            {sections.map((section) => (
              <Card key={section.id} className="border">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0 bg-secondary/20">
                  <div className="flex-1">
                    <Input
                      value={section.title}
                      onChange={(e) => handleSectionTitleChange(section.id!, e.target.value)}
                      className="border-0 bg-transparent p-0 text-sm font-medium focus-visible:ring-0"
                    />
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveSection(section.id!, 'up')}
                      disabled={section.displayOrder === 0}
                      className="h-7 w-7"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveSection(section.id!, 'down')}
                      disabled={section.displayOrder === sections.length - 1}
                      className="h-7 w-7"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteSection(section.id!)}
                      className="h-7 w-7 text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <Textarea
                    placeholder={`Write content for ${section.title}...`}
                    value={section.content}
                    onChange={(e) => handleSectionContentChange(section.id!, e.target.value)}
                    className="min-h-[200px] resize-none"
                  />
                </CardContent>
              </Card>
            ))}
            
            {sections.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No sections yet. Add your first section to get started.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddSection}
                  className="mt-2"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add Section
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}